import {
    AnimatePresence,
    motion
} from 'framer-motion';
import Link from 'next/link';
import { useRef, useState } from 'react';

import { Button } from '@/components/Button';
import NextImage from '@/components/image';
import type { ComponentSectionsFeaturesScrollSection } from '@/generated/graphql-types';
import { Section } from '@/layouts/Section';

export type IFeaturesScrollBlockProps = {
    data: ComponentSectionsFeaturesScrollSection;
};

const itemTitleVariants = {
    active: { opacity: 1, fontSize: '4.8rem' },
    not: { opacity: 0.5, fontSize: '2.4rem' },
};

const itemTitleVariantsTwo = {
    active: { opacity: 1, fontSize: '1.8rem', marginBottom: '1.5rem' },
    not: { opacity: 0.5, fontSize: '1.8rem' },
};

const FeaturesScrollSectionStaticMobile = (props: IFeaturesScrollBlockProps) => {
    const ref = useRef(null);
    const [page, setPage] = useState<number>(0);

    return (
        <Section
            style={{ maxWidth: 'unset', margin: 'unset' }}
            padding={'pt-[5rem] mt-5 relative'}
        >
            <div
                ref={ref}

            >
                <div
                    style={{
                        transition: 'all 0.3s ease-in-out',
                        width: '100%',
                        background:
                            props.data.Features.length > 0
                                ? props.data.Features[page]!.Background
                                : '#151515',
                    }}
                    className={
                        `top-0 flex w-screen max-w-full flex-col items-end justify-between overflow-hidden pb-[6rem] pt-[14.4rem] ${props.data.Features[page]!.Background === '#000000' ? 'text-white' : 'text-black'}`
                    }
                >
                    <div
                        className={
                            `relative self-start border-l-2 ${props.data.ImageType === 'Normal' ? 'border-black' : 'ml-[14vw] min-h-[38rem] border-white'} border-opacity-20 pl-[4.0rem]`
                        }
                    >
                        {props.data.InnerTitle && <h4 className='absolute left-[-0.5rem] top-[-8rem] mb-[6rem] text-[3.2rem]'>{props.data.InnerTitle}</h4>}
                        {props.data.Features.map((item: any, index: number) => (
                            <div
                                key={`Item-${index}`}
                                className={`item relative h-fit max-w-[70vw] pb-12 ${index === page && 'active-item'
                                    }`}
                            >
                                {index === page && (
                                    <motion.div
                                        key={`Item-1-${index}`}
                                        layoutId={`Outline-${index}`}
                                        className={'outline-line'}
                                        initial={true}
                                        style={{
                                            bottom: 0,
                                            position: 'absolute',
                                            left: -42,
                                            top: 0,
                                            // maxHeight: 200,
                                            border: `1px solid ${props.data.ImageType === 'Normal' ? 'black' : props.data.SideLineColor}`,
                                        }}
                                        transition={{}}
                                    />
                                )}

                                <motion.span
                                    key={`Item-2-${index}`}
                                    initial={'not'}
                                    variants={props.data.ImageType === 'Normal' ? itemTitleVariants : itemTitleVariantsTwo}
                                    animate={index === page ? 'active' : 'not'}
                                    transition={{
                                        duration: 0.5,
                                    }}
                                    className={'block tracking-[-0.08rem]'}
                                    dangerouslySetInnerHTML={{ __html: item.Title }}
                                    onClick={() => setPage(index)}
                                />

                                <motion.div
                                    key={`Item-3-${index}`}
                                    style={{ height: index === page ? 'fit-content' : 0 }}
                                >
                                    <AnimatePresence
                                        presenceAffectsLayout={true}
                                        initial={false}
                                        exitBeforeEnter={true}
                                        key={`Item-4-${index}`}
                                    >
                                        {index === page && (
                                            <motion.div
                                                initial={{ height: 0 }}
                                                animate={{ height: 'fit-content' }}
                                                exit={{ opacity: 0 }}
                                                key={`Item-5-${index}`}>
                                                <motion.p
                                                    key={`Item-3-${index}`}
                                                    initial={{ opacity: 0 }}
                                                    animate={{ opacity: props.data.ImageType === 'Normal' ? 1 : 0.5 }}
                                                    exit={{ opacity: 0 }}
                                                    style={{ maxWidth: props.data.ImageType === 'Normal' ? 238 : 400, lineHeight: '2.6rem' }}
                                                    transition={{
                                                        duration: 1,
                                                    }}
                                                    className={'mb-16 text-[1.8rem]'}
                                                >
                                                    {item.Text}
                                                </motion.p>
                                                <motion.div
                                                    key={`Item-4-${index}`}
                                                    initial={{ opacity: 0 }}
                                                    animate={{ opacity: 1 }}
                                                    exit={{ opacity: 0 }}
                                                    transition={{
                                                        duration: 0.3,
                                                    }}
                                                    className={'button'}
                                                >
                                                    {item.ButtonDisabled ? (
                                                        <Button disabled dark={props.data.ImageType === 'Normal'}>
                                                            {item.ButtonText}
                                                        </Button>
                                                    ) : (
                                                        <Link href={item.ButtonLink}>
                                                            <Button dark={props.data.ImageType === 'Normal'}>{item.ButtonText}</Button>
                                                        </Link>
                                                    )}
                                                </motion.div>
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </motion.div>
                            </div>
                        ))}
                    </div>
                    <div
                        className={`relative ${props.data.ImageType === 'Normal' ? 'mr-[5.3vw]' : ''}`}
                        style={{
                            height: '100%',
                            margin: '0 2rem',
                            width: '-webkit-fill-available',
                        }}
                    >
                        <AnimatePresence
                            presenceAffectsLayout={false}
                            initial={true}
                            exitBeforeEnter={true}
                        >
                            {props.data.Features.map((item: any, index: number) => page === index && (
                                <motion.div
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    exit={{ opacity: 0 }}
                                    transition={{ duration: 0.5 }}
                                    key={`$ImWrap-${index}`} className={`${props.data.ImageType === 'Normal' ? '' : 'mt-[8rem]'}`}>
                                    <NextImage
                                        src={''}
                                        priority
                                        media={item ? item.Image : ''}
                                        alt={item ? item.Title : ''}
                                        width={938}
                                        height={703}
                                        layout="responsive"
                                        objectFit='contain'
                                    />
                                </motion.div>
                            ))}
                        </AnimatePresence>
                    </div>
                </div>
                <style jsx>
                    {`
            .outline-line {
              position: absolute;
              top: -20px;
              left: -20px;
              right: -20px;
              bottom: -20px;
              border: 10px solid white;
              border-radius: 50%;
            }

            .item p {
              max-width: 238px;
            }

            .item p,
            .item .button {
              @apply opacity-0;
            }

            .active-item p,
            .active-item .button {
              @apply opacity-100;
            }
          `}
                </style>
            </div>
            {
                props.data.Features.map((item: any, index: number) => page === index && (
                    <div key={`$ImWrap2-${index}`} className={''}>
                        <NextImage
                            src={''}
                            priority
                            media={item.Image}
                            alt={item.Title}
                            width={1}
                            height={1}
                            layout="fixed"
                        />
                    </div>
                ))
            }
        </Section >
    );
};
export { FeaturesScrollSectionStaticMobile };
